<template>
  <section class="p-5" data-cy="housing-table-section">
    <div
      v-if="isAdmin"
      class="flex justify-content-end align-items-center w-12 mb-2"
    >
      <LabelledToggle
        v-if="!isEditingAll"
        v-model="showDeleted"
        text="Show Deleted"
      />
      <div v-if="isEditingAll" class="flex">
        <div class="mr-4 flex">
          <Button
            class="p-button-danger p-button-outlined mr-1"
            icon="far fa-times"
            data-cy="housing-table-cancel-btn"
            @click="handleEquipmentCancelled"
          />
          <div
            v-tooltip="{
              value: 'Housing # or Stage Count Missing',
              disabled: isValid
            }"
            class="h-full"
          >
            <Button
              class="p-button-info p-button-outlined"
              icon="far fa-check-circle"
              :disabled="!isValid"
              data-cy="housing-table-upsert-btn"
              @click="handleUpsert"
            />
          </div>
        </div>

        <Button
          class="p-button-successs"
          icon="far fa-plus-circle"
          data-cy="housing-table-row-create-btn"
          @click="handleRowCreate"
        />
      </div>
      <div v-else class="flex mx-list-4">
        <Button
          v-if="showDeleted"
          class="p-button-success p-button-outlined mr-1"
          :disabled="disableRestoreDelete || !online"
          @click="handleRestore"
          >Restore</Button
        >
        <Button
          v-else
          class="p-button-danger p-button-outlined mr-1"
          :disabled="disableRestoreDelete || !online"
          @click="handleDelete"
          >Delete</Button
        >
        <Button
          class="p-button-info p-button-outlined"
          icon="far fa-pencil"
          :disabled="showDeleted || !online"
          @click="handleRowEditInit"
        />
      </div>
    </div>

    <DataTable
      class="p-datatable-sm editable-cells-table"
      dataKey="id"
      editMode="row"
      paginator-template="CurrentPageReport PrevPageLink PageLinks NextPageLink"
      :selection="isAdmin ? flagged : undefined"
      :row-class="
        (data: any) => (data.id === selectedEquipmentId ? 'p-highlight' : '')
      "
      :first="(itemsPerPage || 0) * (itemsPageNum || 0)"
      :rows="itemsPerPage"
      :total-records="totalCount ?? 0"
      :value="isEditingAll ? editingRows : filteredRows"
      :selectionMode="isAdmin ? undefined : 'single'"
      :current-page-report-template="totalCount ? '{first} - {last}' : ''"
      data-cy="housing-table-data-table"
      @update:selection="flagged = $event"
      @row-select="handleRowSelect"
    >
      <Column
        v-if="isAdmin && !isEditingAll"
        :frozen="true"
        selectionMode="multiple"
      ></Column>

      <Column v-if="isAdmin && isEditingAll">
        <template #body="{ data, index }">
          <Button
            v-if="data.created_at === 'now()'"
            icon="far fa-minus"
            class="p-button-rounded p-button-secondary button-xs"
            @click="editingRows && editingRows.splice(index, 1)"
          />
        </template>
      </Column>

      <Column
        v-for="(column, colIndex) in columns"
        :key="column.field"
        :field="column.field"
        :header="column.header"
        :sortable="column.sortable"
        :class="column.class"
        :frozen="column.frozen"
      >
        <template #body="{ data, field, index: rowIndex }">
          <div v-if="isEditingAll">
            <div v-if="editingRows && column.editable">
              <ConverterInput
                v-if="column.isNumber"
                v-model:model-value="
                  editingRows[rowIndex][field as keyof Housings]
                "
                label=""
                :disabled="isSubmitting"
                :base-unit="GenericUnits.Generic"
                :model="GenericUnits.options()"
                :decimal-places="column.decimalPlaces"
                mode="write"
              />
              <Checkbox
                v-else-if="column.isCheckbox"
                v-model:model-value="data[field]"
                v-tooltip.top="{
                  value:
                    '\'KOC Only\' and \'Ecopetrol Only\' cannot both be selected.',
                  disabled: !eitherKOCorEcopetrolCheck(data, field)
                }"
                :disabled="eitherKOCorEcopetrolCheck(data, field)"
                :true-value="data[field] || 'now()'"
                :false-value="null"
                binary
              />
              <InputText
                v-else
                v-model="editingRows[rowIndex][field as keyof Housings]"
                :autofocus="colIndex === 0"
              />
            </div>
          </div>
          <div v-if="!isEditingAll">
            <span v-if="column.isCheckbox && data[field]">YES</span>
            <span v-else-if="column.isCheckbox && !data[field]">NO</span>
            <span v-else> {{ data[field] }}</span>
          </div>
        </template>
      </Column>

      <template #empty>
        <p class="text-center m-5">0 {{ title }} Found</p>
      </template>
    </DataTable>
  </section>
</template>

<script setup lang="ts">
import { computed, inject, isRef, ref, toRefs } from "vue";
import { useOnline } from "@vueuse/core";
import ConverterInput from "@/components/ConverterInput.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import type { BaseEquipment } from "@/composables/equipment/equipmentTypes";
import { useHousings } from "@/composables/equipment/housings/housings";
import { GenericUnits } from "@/libs/units/generic";
import type { Housings } from "gql/api";
import type { PropType } from "vue";

const props = defineProps({
  isAdmin: { type: Boolean, required: true },
  pumpId: { type: String, required: true },
  pumpHousings: {
    type: Array as PropType<Housings[]>,
    default: () => []
  }
});

const { isAdmin, pumpId } = toRefs(props);

const emit = defineEmits<{
  (event: "select", equipment: Housings): void;
}>();

const housingsData = useHousings(
  isAdmin.value,
  pumpId.value,
  computed(() => props.pumpHousings)
);

const selectedEquipmentId = inject("selected-equipment-id", ref(""));

const online = useOnline();

// ignore clicks on already selected row
function handleRowSelect(newSelectedEquipment: { data: BaseEquipment }) {
  if (newSelectedEquipment.data.id !== selectedEquipmentId.value) {
    emit("select", newSelectedEquipment.data as Housings);
  }
}
function eitherKOCorEcopetrolCheck(
  data: BaseEquipment,
  field: string
): boolean {
  const KOC = "only_koc_timestamp";
  const ECO = "only_ecopetrol_timestamp";
  const test = field === KOC || field === ECO;

  // exit early if not KOC or Eciopetrol
  if (!test) {
    return false;
  }
  if (field && field === KOC) {
    return !!data[ECO] && data[ECO] !== null;
  }
  if (field && field === ECO) {
    return !!data[KOC] && data[KOC] !== null;
  }
  // safety
  return false;
}

const {
  isEditingAll,
  columns: columnsRaw,
  showDeleted,
  flagged,
  editingRows,
  itemsPerPage,
  itemsPageNum,
  totalCount,
  filteredRows,
  handleRestore,
  handleDelete,
  handleRowEditInit,
  handleUpsert,
  handleEquipmentCancelled,
  handleRowCreate,
  title,
  isSubmitting,
  isValid,
  disableRestoreDelete
} = housingsData;

// Handle both ComputedRef and plain array for backward compatibility
const columns = computed(() => {
  return isRef(columnsRaw) ? columnsRaw.value : columnsRaw;
});
</script>
